<template lang="pug">
  .select-container(v-on-clickaway='hideOptions')
    button.select-button(
      :disabled='disabled || loading'
      :class='{disabled, error: showError}'
      type='button'
      @click='toggleOptions'
    )
      span.text-normal.dark-gray {{ $t("topics.subtopicsPlaceholder") }}
      simple-svg.m-left-05(
        :filepath='arrowDownIcon'
        :class='{ open: showOptions }'
        width='18px'
        height='18px'
      )
    .options-container.kimple-scroll(v-if='showOptions')
      button.topic(v-for='topic in options' type='button' :key='topic.value' @click='showTopic(topic)')
        .label.row.middle.space-between
          checkbox.m-right-1(
            transparent
            :label='topic.label'
            :name='topic.label'
            :checked='isTopicSelected(topic)'
            :updateCheck='() => toggleTopic(topic)'
          )
          simple-svg(
            v-if='showSubtopics(topic)'
            :filepath='arrowDownIcon'
            :class='{ open: activeTopics.includes(topic.value) }'
            alt='show options'
            width='18px'
            height='18px'
          )
        button.subtopic.row.middle(
          v-if='showSubtopics(topic) && activeTopics.includes(topic.value)'
          v-for='subtopic in topic.subtopics'
          :key='subtopic.value' type='button'
          @click.stop
        )
          checkbox.m-right-1(
            transparent
            :label='subtopic.label'
            :name='subtopic.label'
            :checked='selected.includes(subtopic.value)'
            :updateCheck='() => toggleSubtopic(subtopic)'
          )
    .row.top.m-top-1(v-if='showError')
      simple-svg.error-icon.red-icon.m4-right-2.red-stroke-icon(:filepath='errorIcon' :width='"16px"' :height='"16px"')
      span.text-small.red {{ errorMessage }}
</template>

<script>
import arrowDownIcon from '@/assets/ic_chevron-down.svg'
import errorIcon from '@/assets/ic_stop.svg'
import { mapActions, mapState } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway'
import inputValidation from '@/mixins/inputsValidationMixin'
import Checkbox from '../../Inputs/Checkbox'
import { otherSubtopicId } from '@/constants/topics'

export default {
  mixins: [inputValidation, clickaway],
  components: { Checkbox },
  props: {
    disabled: { type: Boolean },
    value: { type: Array },
    errorMessage: { type: String }
  },
  data() {
    return {
      arrowDownIcon,
      errorIcon,
      showOptions: false,
      loading: false,
      activeTopics: [],
      selected: []
    }
  },
  async created() {
    if (this.value) {
      this.selected = this.value
      this.activeTopics = this.topics
        .filter(top => top.subtopics.filter(sub => this.selected.includes(sub.id)).length)
        .map(top => top.id)
    }
  },
  watch: {
    value() {
      if (this.value) {
        this.selected = this.value
      }
    },
    showOptions(newValue) {
      if (!newValue) this.$emit('close')
    }
  },
  computed: {
    ...mapState({
      topics: state => state.dashboard.topics
    }),
    options() {
      return this.topics
        .map(topic => ({
          label: topic.name,
          value: topic.id,
          subtopics: topic.subtopics
            .map(subtopic => ({
              label: subtopic.name,
              value: subtopic.id
            }))
        }))
    }
  },
  methods: {
    ...mapActions([
      'showElement'
    ]),
    hideOptions() {
      this.showOptions = false
      if (!this.showOptions) this.verifyValidation()
    },
    toggleOptions() {
      this.showOptions = !this.showOptions
      if (!this.showOptions) this.verifyValidation()
    },
    showSubtopics(topic) {
      return topic.subtopics.filter(sub => sub.label !== topic.label).length
    },
    showTopic(topic) {
      if (this.activeTopics.includes(topic.value)) {
        this.activeTopics = this.activeTopics.filter(top => top !== topic.value)
      } else {
        this.activeTopics = [...this.activeTopics, topic.value]
      }
    },
    isTopicSelected(topic) {
      return Boolean(topic.subtopics.length && topic.subtopics.some(sub => this.selected.includes(sub.value)))
    },
    toggleTopic(topic) {
      if (this.isTopicSelected(topic)) {
        this.selected = this.selected
          .filter(subValue =>
            !topic.subtopics
              .map(sub => sub.value)
              .includes(subValue)
          )
      } else {
        const maxReached = Boolean((this.selected.length + topic.subtopics.filter(sub => !this.selected.includes(sub.value)).length) > 3)
        if (maxReached) return
        this.selected = [...this.selected, ...topic.subtopics.map(sub => sub.value)]
        if (topic.subtopics.find(sub => sub.value === otherSubtopicId)) {
          this.showElement({ element: 'suggestTopicModal' })
        }
      }
      this.$emit('change', this.selected)
    },
    toggleSubtopic(subtopic) {
      if (this.selected.includes(subtopic.value)) {
        this.selected = this.selected.filter(sub => sub !== subtopic.value)
      } else {
        if (this.selected.length === 3) return
        this.selected = [...this.selected, subtopic.value]
      }
      this.$emit('change', this.selected)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/z-indexes';
@import 'src/scss/common/forms';

.select-container {
  position: relative;
  width: 100%;
}

.select-button {
  @extend .select-base;
  align-items: center;
  display: flex;
  justify-content: space-between;
  text-align: left;
  height: 30px;
  padding: 12px 10px;
  margin: 0;
  background-color: $light-gray;
  &.disabled {
    background-color: $light-gray;
    cursor: default;
  }
}

.open {
  transform: rotate(180deg);
}

.direction {
  opacity: 0.6;
}

.options-container {
  background-color: $white;
  border: 1px solid $light-gray;
  border-radius: 6px;
  box-shadow: 0 2px 8px 0 $shadow-gray;
  overflow-y: auto;
  position: absolute;
  top: calc(100% + 5px);
  max-width: var(--container-size);
  z-index: $select-options;
  width: 100%;
  max-height: 300px;
}
.error {
  border: 1px solid $red;
}

.topic {
  color: $black;
  font-size: 16px;
  width: 100%;
  .label{
    padding: 8px 10px;
    &:hover {
      background-color: $light-gray;
    }
  }
  &.disabled{
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.subtopic{
  color: $black;
  font-size: 16px;
  padding: 8px 10px;
  padding-left: 25px;
  width: 100%;
  &:hover {
    background-color: $light-gray;
  }
  &.disabled{
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
